import React, { useState, useEffect } from 'react';
import {
  Layout,
  Menu,
  Typography,
  Button,
  Card,
  List,
  Avatar,
  Row,
  Col,
  Statistic,
  Badge,
  Modal,
  message,
  Progress,
} from 'antd';
import {
  DashboardOutlined,
  TeamOutlined,
  BookOutlined,
  ProjectOutlined,
  TrophyOutlined,
  MessageOutlined,
  FileTextOutlined,
  SettingOutlined,
  LogoutOutlined,
  PlusOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios'; // Adjust path to your Axios instance

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { SubMenu } = Menu;

const MentorDashboard = () => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mentees, setMentees] = useState([]);
  const [courses, setCourses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [messages, setMessages] = useState([]);
  const [resources, setResources] = useState([]);

  // Sample data for initial render (replace with API calls)
  const initialMentees = [
    { id: 1, name: 'John Doe', progress: 75, lastContact: '2025-03-04' },
    { id: 2, name: 'Alice Brown', progress: 50, lastContact: '2025-03-03' },
  ];

  const initialCourses = [
    { id: 1, title: 'AI Basics', menteesEnrolled: 10, status: 'Active' },
    { id: 2, title: 'Cybersecurity 101', menteesEnrolled: 5, status: 'Draft' },
  ];

  const initialProjects = [
    { id: 1, title: 'AI Chatbot', menteeId: 1, status: 'In Progress', deadline: '2025-04-01' },
    { id: 2, title: 'Portfolio Website', menteeId: 2, status: 'Completed', deadline: '2025-03-01' },
  ];

  const initialChallenges = [
    { id: 1, title: 'AI Image Recognition', menteeId: 1, status: 'Active', daysLeft: 20 },
    { id: 2, title: 'Cybersecurity Challenge', menteeId: 2, status: 'Submitted', daysLeft: 0 },
  ];

  const initialMessages = [
    { id: 1, menteeId: 1, content: 'Need help with my project', date: '2025-03-04 10:00' },
    { id: 2, menteeId: 2, content: 'Submitted my challenge!', date: '2025-03-03 15:00' },
  ];

  const initialResources = [
    { id: 1, title: 'AI Tutorial PDF', type: 'PDF', uploaded: '2025-03-01' },
    { id: 2, title: 'Cybersecurity Video', type: 'Video', uploaded: '2025-03-02' },
  ];

  // Fetch data from backend
  useEffect(() => {
    // Simulate API calls
    setMentees(initialMentees);
    setCourses(initialCourses);
    setProjects(initialProjects);
    setChallenges(initialChallenges);
    setMessages(initialMessages);
    setResources(initialResources);

    // Real API calls (uncomment when ready)
    /*
    api.get('/mentor/mentees').then((response) => setMentees(response.data)).catch(console.error);
    api.get('/mentor/courses').then((response) => setCourses(response.data)).catch(console.error);
    api.get('/mentor/projects').then((response) => setProjects(response.data)).catch(console.error);
    api.get('/mentor/challenges').then((response) => setChallenges(response.data)).catch(console.error);
    api.get('/mentor/messages').then((response) => setMessages(response.data)).catch(console.error);
    api.get('/mentor/resources').then((response) => setResources(response.data)).catch(console.error);
    */
  }, []);

  const handleMenuClick = (e) => {
    switch (e.key) {
      case 'dashboard':
        navigate('/mentor/dashboard');
        break;
      case 'mentees-all':
        navigate('/mentor/mentees');
        break;
      case 'mentees-progress':
        navigate('/mentor/mentees/progress');
        break;
      case 'courses-all':
        navigate('/mentor/courses');
        break;
      case 'courses-managed':
        navigate('/mentor/courses/managed');
        break;
      case 'projects-all':
        navigate('/mentor/projects');
        break;
      case 'projects-active':
        navigate('/mentor/projects/active');
        break;
      case 'projects-completed':
        navigate('/mentor/projects/completed');
        break;
      case 'challenges-all':
        navigate('/mentor/challenges');
        break;
      case 'challenges-active':
        navigate('/mentor/challenges/active');
        break;
      case 'challenges-submitted':
        navigate('/mentor/challenges/submitted');
        break;
      case 'messages-all':
        navigate('/mentor/messages');
        break;
      case 'messages-unread':
        navigate('/mentor/messages/unread');
        break;
      case 'resources-all':
        navigate('/mentor/resources');
        break;
      case 'resources-upload':
        navigate('/mentor/resources/upload');
        break;
      case 'settings':
        navigate('/mentor/settings');
        break;
      case 'logout':
        localStorage.removeItem('userRole');
        navigate('/signin');
        break;
      default:
        break;
    }
  };

  const handleAddResource = () => {
    navigate('/mentor/resources/upload');
  };

  const handleSendMessage = (menteeId) => {
    Modal.confirm({
      title: 'Send Message',
      content: 'Would you like to send a message to this mentee?',
      onOk: () => {
        navigate(`/mentor/messages/new?menteeId=${menteeId}`);
      },
    });
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Left Sidebar with Child Items */}
      <Sider collapsible collapsed={collapsed} onCollapse={(value) => setCollapsed(value)}>
        <div style={{ height: 32, margin: 16, background: 'rgba(255, 255, 255, 0.2)' }} /> {/* Logo placeholder */}
        <Menu
          theme="dark"
          defaultSelectedKeys={['dashboard']}
          mode="inline"
          onClick={handleMenuClick}
        >
          <Menu.Item key="dashboard" icon={<DashboardOutlined />}>
            Dashboard
          </Menu.Item>
          <SubMenu key="mentees" icon={<TeamOutlined />} title="Mentees">
            <Menu.Item key="mentees-all">All Mentees</Menu.Item>
            <Menu.Item key="mentees-progress">Progress Tracking</Menu.Item>
          </SubMenu>
          <SubMenu key="courses" icon={<BookOutlined />} title="Courses">
            <Menu.Item key="courses-all">All Courses</Menu.Item>
            <Menu.Item key="courses-managed">Managed Courses</Menu.Item>
          </SubMenu>
          <SubMenu key="projects" icon={<ProjectOutlined />} title="Projects">
            <Menu.Item key="projects-all">All Projects</Menu.Item>
            <Menu.Item key="projects-active">Active Projects</Menu.Item>
            <Menu.Item key="projects-completed">Completed Projects</Menu.Item>
          </SubMenu>
          <SubMenu key="challenges" icon={<TrophyOutlined />} title="Challenges">
            <Menu.Item key="challenges-all">All Challenges</Menu.Item>
            <Menu.Item key="challenges-active">Active Challenges</Menu.Item>
            <Menu.Item key="challenges-submitted">Submitted Challenges</Menu.Item>
          </SubMenu>
          <SubMenu key="messages" icon={<MessageOutlined />} title="Messages">
            <Menu.Item key="messages-all">All Messages</Menu.Item>
            <Menu.Item key="messages-unread">Unread Messages</Menu.Item>
          </SubMenu>
          <SubMenu key="resources" icon={<FileTextOutlined />} title="Resources">
            <Menu.Item key="resources-all">All Resources</Menu.Item>
            <Menu.Item key="resources-upload">Upload Resource</Menu.Item>
          </SubMenu>
          <Menu.Item key="settings" icon={<SettingOutlined />}>
            Settings
          </Menu.Item>
          <Menu.Item key="logout" icon={<LogoutOutlined />}>
            Logout
          </Menu.Item>
        </Menu>
      </Sider>

      {/* Main Layout */}
      <Layout className="site-layout">
        {/* Header */}
        <Header
          style={{
            padding: '0 20px',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Title level={3} style={{ margin: 0 }}>
            Mentor Dashboard
          </Title>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAddResource}>
            Add Resource
          </Button>
        </Header>

        {/* Content */}
        <Content style={{ margin: '24px 16px', padding: 24, background: '#fff', minHeight: 280 }}>
          <Row gutter={[16, 16]}>
            {/* Overview Stats */}
            <Col span={24}>
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic title="Mentees" value={mentees.length} prefix={<TeamOutlined />} />
                </Col>
                <Col span={6}>
                  <Statistic title="Managed Courses" value={courses.length} prefix={<BookOutlined />} />
                </Col>
                <Col span={6}>
                  <Statistic title="Active Projects" value={projects.filter((p) => p.status === 'In Progress').length} prefix={<ProjectOutlined />} />
                </Col>
                <Col span={6}>
                  <Statistic title="Unread Messages" value={messages.filter((m) => !m.read).length} prefix={<MessageOutlined />} />
                </Col>
              </Row>
            </Col>

            {/* Mentees List */}
            <Col span={16}>
              <Card title="Your Mentees" bordered={false}>
                <List
                  itemLayout="horizontal"
                  dataSource={mentees.slice(0, 5)}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <Button type="link" onClick={() => navigate(`/mentor/mentees/${item.id}`)}>
                          View
                        </Button>,
                        <Button type="link" onClick={() => handleSendMessage(item.id)}>
                          Message
                        </Button>,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Avatar icon={<UserOutlined />} />}
                        title={item.name}
                        description={
                          <>
                            <Text>{`Last Contact: ${item.lastContact}`}</Text>
                            <Progress percent={item.progress} size="small" style={{ marginTop: 8 }} />
                          </>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Card>

              {/* Courses List */}
              <Card title="Managed Courses" bordered={false} style={{ marginTop: 16 }}>
                <List
                  itemLayout="horizontal"
                  dataSource={courses.slice(0, 5)}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <Button type="link" onClick={() => navigate(`/mentor/courses/${item.id}`)}>
                          View
                        </Button>,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Avatar icon={<BookOutlined />} />}
                        title={item.title}
                        description={`Mentees Enrolled: ${item.menteesEnrolled} | Status: ${item.status}`}
                      />
                    </List.Item>
                  )}
                />
              </Card>

              {/* Projects List */}
              <Card title="Mentee Projects" bordered={false} style={{ marginTop: 16 }}>
                <List
                  itemLayout="horizontal"
                  dataSource={projects.slice(0, 5)}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <Button type="link" onClick={() => navigate(`/mentor/projects/${item.id}`)}>
                          View
                        </Button>,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Avatar icon={<ProjectOutlined />} />}
                        title={item.title}
                        description={`Mentee: ${mentees.find((m) => m.id === item.menteeId)?.name || 'Unknown'} | Status: ${item.status} | Deadline: ${item.deadline}`}
                      />
                    </List.Item>
                  )}
                />
              </Card>
            </Col>

            {/* Right Sidebar */}
            <Col span={8}>
              <Card title="Recent Messages" bordered={false} style={{ marginBottom: 16 }}>
                <List
                  dataSource={messages.slice(0, 5)}
                  renderItem={(item) => (
                    <List.Item>
                      <Badge
                        status="processing"
                        text={`${mentees.find((m) => m.id === item.menteeId)?.name || 'Unknown'}: ${item.content} - ${item.date}`}
                      />
                    </List.Item>
                  )}
                />
              </Card>

              <Card title="Mentee Challenges" bordered={false} style={{ marginBottom: 16 }}>
                <List
                  dataSource={challenges.slice(0, 5)}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <Button type="link" onClick={() => navigate(`/mentor/challenges/${item.id}`)}>
                          Review
                        </Button>,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Avatar icon={<TrophyOutlined />} />}
                        title={item.title}
                        description={`Mentee: ${mentees.find((m) => m.id === item.menteeId)?.name || 'Unknown'} | Status: ${item.status}`}
                      />
                    </List.Item>
                  )}
                />
              </Card>

              <Card title="Learning Resources" bordered={false}>
                <List
                  dataSource={resources.slice(0, 3)}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <Button type="link" onClick={() => navigate(`/mentor/resources/${item.id}`)}>
                          View
                        </Button>,
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Avatar icon={<FileTextOutlined />} />}
                        title={item.title}
                        description={`Type: ${item.type} | Uploaded: ${item.uploaded}`}
                      />
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
          </Row>
        </Content>
      </Layout>
    </Layout>
  );
};

export default MentorDashboard;